/**
 * Skeleton → content morph. A loading skeleton can't match every shape of
 * real data, so instead of swapping one for the other we record where the
 * skeleton's blocks sit, and when the real content first renders each block
 * glides and resizes onto the real element it stood in for, then fades as
 * the content fades in.
 *
 * Each block morphs onto at most one target (the caller pairs them, by role
 * or reading order). A block with no target fades where it stands: folding
 * several blocks into one row stacked them into a squashed band.
 */

export const SKELETON_BLOCK_ATTR = 'data-skeleton-block';

type RecordedBlock = {
  /** The block's `data-skeleton-block` value: what it stands in for. */
  role: string;
  rect: DOMRect;
  radius: string;
  background: string;
  /** A copy of the block, so the ghost looks exactly like the skeleton. */
  node: HTMLElement;
};

type Recording = { blocks: RecordedBlock[]; seenAt: number };

/** Independent surfaces that each swap a skeleton for content. */
export type SkeletonMorphChannel = 'transcript' | 'sidebar';

/** A skeleton seen longer ago than this was not what the user just saw. */
const RECORDING_FRESH_MS = 1200;
const MORPH_MS = 320;
const FADE_MS = 160;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const latest = new Map<SkeletonMorphChannel, Recording>();

function isVisible(rect: DOMRect): boolean {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.top < window.innerHeight
  );
}

/** Records the visible blocks of a mounted skeleton. Call on layout changes. */
export function recordSkeletonBlocks(
  root: HTMLElement,
  channel: SkeletonMorphChannel = 'transcript'
): void {
  const blocks: RecordedBlock[] = [];
  for (const element of root.querySelectorAll<HTMLElement>(
    `[${SKELETON_BLOCK_ATTR}]`
  )) {
    const node = element.cloneNode(true);
    if (!(node instanceof HTMLElement)) continue;
    const style = window.getComputedStyle(element);
    blocks.push({
      role: element.getAttribute(SKELETON_BLOCK_ATTR) ?? '',
      rect: element.getBoundingClientRect(),
      radius: style.borderRadius,
      background: style.backgroundColor,
      node,
    });
  }
  const visible = blocks.filter((block) => isVisible(block.rect));
  if (visible.length === 0) return;
  latest.set(channel, { blocks: visible, seenAt: performance.now() });
}

/** Marks the recorded skeleton as still on screen (it is unmounting now). */
export function touchSkeletonRecording(
  channel: SkeletonMorphChannel = 'transcript'
): void {
  const recording = latest.get(channel);
  if (recording) recording.seenAt = performance.now();
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Whether content mounting now replaces a skeleton the user was looking at.
 * Consumes the recording, so each skeleton morphs at most once.
 */
export type { RecordedBlock as SkeletonMorphBlock };

export function takeFreshSkeletonRecording(
  channel: SkeletonMorphChannel = 'transcript'
): RecordedBlock[] | null {
  const recording = latest.get(channel);
  latest.delete(channel);
  if (!recording) return null;
  if (performance.now() - recording.seenAt > RECORDING_FRESH_MS) return null;
  if (prefersReducedMotion()) return null;
  return recording.blocks;
}

function placeGhost(block: RecordedBlock): HTMLElement {
  const ghost = block.node;
  ghost.removeAttribute(SKELETON_BLOCK_ATTR);
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    margin: '0',
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'fixed',
    left: `${block.rect.left}px`,
    top: `${block.rect.top}px`,
    width: `${block.rect.width}px`,
    height: `${block.rect.height}px`,
    borderRadius: block.radius,
    background: block.background,
    pointerEvents: 'none',
    zIndex: '40',
  });
  document.body.appendChild(ghost);
  return ghost;
}

/** Stand-ins for the skeleton blocks, shown at once so no frame is blank. */
export function placeSkeletonGhosts(blocks: RecordedBlock[]): HTMLElement[] {
  return blocks.map(placeGhost);
}

const SETTLE_MAX_FRAMES = 24;

function rowsSignature(rects: Array<DOMRect | null>): string {
  return rects
    .map((rect) =>
      rect ? `${Math.round(rect.top)}:${Math.round(rect.height)}` : '-'
    )
    .join('|');
}

/**
 * Waits until `getTargets()` stops moving (the opening scroll and late
 * layout settle a few frames after mount), then morphs each ghost onto its
 * target while `content` (hidden by the caller) fades in.
 */
export function settleThenMorph(
  blocks: RecordedBlock[],
  ghosts: HTMLElement[],
  getTargets: () => Array<HTMLElement | null>,
  content: HTMLElement
): void {
  let previous = '';
  let stableFrames = 0;
  let frames = 0;
  const tick = () => {
    const rects = getTargets().map((target) => {
      const rect = target?.getBoundingClientRect();
      return rect && isVisible(rect) ? rect : null;
    });
    const signature = rowsSignature(rects);
    stableFrames = signature === previous ? stableFrames + 1 : 0;
    previous = signature;
    frames += 1;
    if (stableFrames >= 2 || frames >= SETTLE_MAX_FRAMES) {
      morphGhosts(blocks, ghosts, rects, content);
      return;
    }
    window.requestAnimationFrame(tick);
  };
  window.requestAnimationFrame(tick);
}

function morphGhosts(
  blocks: RecordedBlock[],
  ghosts: HTMLElement[],
  targetRects: Array<DOMRect | null>,
  content: HTMLElement
): void {
  const reveal = content.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: FADE_MS + 120,
    delay: MORPH_MS * 0.55,
    easing: 'ease-out',
    fill: 'backwards',
  });
  reveal.onfinish = () => {
    content.style.opacity = '';
  };
  content.style.opacity = '';

  for (const [index, ghost] of ghosts.entries()) {
    const block = blocks[index];
    const to = targetRects[index];
    if (!block || !to) {
      ghost.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: FADE_MS,
        fill: 'forwards',
      }).onfinish = () => ghost.remove();
      continue;
    }
    const dx = to.left - block.rect.left;
    const dy = to.top - block.rect.top;
    const morph = ghost.animate(
      [
        {
          transform: 'translate(0, 0)',
          width: `${block.rect.width}px`,
          height: `${block.rect.height}px`,
          opacity: 1,
        },
        {
          transform: `translate(${dx}px, ${dy}px)`,
          width: `${to.width}px`,
          height: `${to.height}px`,
          opacity: 1,
          offset: 0.7,
        },
        {
          transform: `translate(${dx}px, ${dy}px)`,
          width: `${to.width}px`,
          height: `${to.height}px`,
          opacity: 0,
        },
      ],
      { duration: MORPH_MS + FADE_MS, easing: EASE, fill: 'forwards' }
    );
    const remove = () => ghost.remove();
    morph.onfinish = remove;
    morph.oncancel = remove;
  }
}
